import { AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { getToolTypeLabel, ToolType } from '@backrs_ui/common-ui/src/models/tool';

import { NuggetUXVariant } from '../NuggetUITypes';
import { TypeChipProps } from '../TypeChip';

const ROLE_TYPE_LABEL_MAP: Record<string, string> = {
  'Junior IC': 'Full-time',
  Intern: 'Internship',
};

export const getToolTypeChip = (toolType?: ToolType): TypeChipProps => ({
  label: getToolTypeLabel(toolType),
  icon: 'BookOpen01',
});

/**
 * Returns the appropriate type chip props for a given nugget variant
 */
export const getTypeChipPropsForVariant = (variant: NuggetUXVariant): TypeChipProps[] => {
  switch (variant.type) {
    case 'opportunity':
      return [{ label: 'Opportunity', icon: 'Building01' }];

    case 'event':
      return [
        { label: 'Event', icon: 'CalendarPlus01' },
        { label: 'Event Series', icon: 'Calendar' },
      ];

    case 'video-external':
      return [{ label: 'Video', icon: 'VideoRecorder' }];

    case 'video-internal':
      return [
        { label: 'Video', icon: 'VideoRecorder' },
        { label: 'Real People', icon: 'User02' },
      ];

    case 'article':
      return [{ label: 'Article', icon: 'File06' }];

    case 'institution':
      return [{ label: 'University', icon: 'Bank' }];

    case 'employer':
      return [{ label: 'Employer', icon: 'File02' }];

    case 'audio':
      return [
        { label: 'Audio', icon: 'VolumeMin' },
        { label: 'Resource', icon: 'BookOpen01' },
      ];

    case 'document':
      return [{ label: 'Document', icon: 'File02' }];

    case 'resource':
      return [{ label: 'Resource', icon: 'BookOpen01' }];

    case 'text-response':
      return [{ label: 'Resource', icon: 'BookOpen01' }];

    case 'book':
      return [{ label: 'Book', icon: 'BookOpen01' }];

    case 'competency':
      return [{ label: variant.categoryName || 'Competency', icon: 'Lightbulb05' }];

    case 'scholarships':
      return [{ label: 'Scholarship', icon: 'CurrencyDollarCircle' }];

    case 'internship': {
      const firstCareerPath = variant.internshipInfo.careerPaths?.[0];
      const careerCategoryChip: TypeChipProps[] = firstCareerPath?.career_category?.short_name
        ? [
            {
              label: firstCareerPath.career_category.short_name,
              icon: 'Atom01',
            },
          ]
        : [];

      return [
        {
          label:
            ROLE_TYPE_LABEL_MAP[variant.internshipInfo.role_type ?? ''] ??
            variant.internshipInfo.role_type ??
            'Opportunity',
          icon: 'Briefcase01',
        },
        ...careerCategoryChip,
      ];
    }

    case 'career-path':
      return [
        { label: 'Career Path', icon: 'Rocket02' },
        { label: 'Career Major', icon: 'Atom01' },
      ];

    case 'career-track':
      return [
        { label: 'Career Track', icon: 'Rocket02' },
        { label: 'Track', icon: 'Atom01' },
      ];

    case 'generated-content':
      return [
        { label: 'For You', icon: 'File02' },
        ...(variant.generatedContentInfo?.minor_source
          ? [
              {
                label: variant.generatedContentInfo?.minor_source ?? '',
                icon: 'File02' as AppIcons,
              },
            ]
          : []),
      ];

    case 'track-assessment-result':
      return [{ label: 'For You', icon: 'File02' }];

    case 'track-assessment-unlock':
      return [
        { label: 'Four Unlocks', icon: 'LockKeyholeSquare' },
        { label: variant.trackAssessmentUnlock?.category?.shortname ?? '', icon: 'Atom01' },
      ];

    case 'educational-investment':
      return [];

    case 'tool':
      return [getToolTypeChip(variant.toolInfo?.tool_type)];

    case 'major':
      return [{ label: 'Academics', icon: 'CurrencyDollarCircle' }];

    case 'industry':
      return [{ label: 'Industry', icon: 'ShieldDollar' }];

    case 'profile-external':
    case 'profile-internal':
      return [];

    case 'coach':
      return [{ label: 'Coach', icon: 'User02' }];

    case 'deep-dive':
      return [{ label: 'Deep Dive', icon: 'BookOpen01' }];

    default:
      return [];
  }
};
